/**
 * Eigenbau: Der Seitenkopf — eine Stelle, an der jede Überschrift beginnt.
 *
 * Jede Seite beginnt ihre Überschrift an derselben Stelle. Vorher taten sie
 * das nicht, und zwar messbar. Acht Seiten, vier Bauformen:
 *
 * - `Klaerungen` und `UebersichtReiter` setzten `px-9 py-14` **zusätzlich** zur
 *   Polsterung der Hülle. Ihre Titel saßen dadurch 56px tiefer und 36px weiter
 *   rechts als alle anderen.
 * - Die Kontextzeile darüber gab es auf manchen Seiten (in Grad 11.5 **oder**
 *   12), auf anderen nicht — der Titel begann also je Seite auf anderer Höhe.
 * - Der Abstand zwischen Zeile und Titel war `mt-1.5` oder `mt-3.5`.
 *
 * Dieser Baustein beantwortet all das an einer Stelle:
 *
 * 1. **Keine eigene Polsterung.** Die Hülle liefert `lg:px-9 lg:pt-14
 *    lg:pb-24` — die 56/36/96px der Vorlage. Wer hier noch einmal polstert,
 *    verschiebt seine Seite gegen alle anderen.
 * 2. **Die Kontextzeile belegt ihren Platz immer**, auch wenn sie leer ist.
 *    Sonst rutscht der Titel auf Seiten ohne Kontext nach oben, und genau das
 *    war der sichtbare Bruch. Reserviert wird über `min-h`, nicht über ein
 *    Leerzeichen — ein `&nbsp;` würde von Vorleseprogrammen mitgelesen.
 * 3. **Ein Abstand, ein Grad.** 14px zwischen Zeile und Titel, wie auf „Heute"
 *    zwischen Datum und Titel.
 */

import type { ReactNode } from "react";
import { cx } from "@/lib/utils";
import { Titel } from "@/components/entwurf/Titel";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";

/** Die Breiten, die der Entwurf für Inhaltsspalten benutzt. */
const BREITEN = {
  /** Listen und Kennzahlbänder — die Vorgabe. */
  1160: "max-w-[1160px]",
  /** Ereignisströme und Fließtext. */
  880: "max-w-[880px]",
  /** Detailspalten neben einer Nebenspalte. */
  720: "max-w-[720px]",
  /** Leerzustände und kurze Meldungen. */
  560: "max-w-[560px]",
} as const;

export function Seitenkopf({
  kontext,
  children,
  grad = 46,
  aktion,
  breite = 1160,
  className,
}: {
  /**
   * Die Mono-Zeile über dem Titel — Bereich, Projekt oder Datum. Bleibt sie
   * offen, wird ihr Platz trotzdem freigehalten, damit der Titel nicht wandert.
   */
  kontext?: ReactNode;
  children: ReactNode;
  /**
   * 46 für Bereichsseiten, 54 nur für „Heute". Der Entwurf hält diese
   * Abstufung; die **Position** ist davon unberührt, weil beide Grade an
   * derselben Oberkante beginnen.
   */
  grad?: 40 | 46 | 54;
  /** Rechts neben dem Titel, etwa ein Verweis oder ein Schalter. */
  aktion?: ReactNode;
  breite?: keyof typeof BREITEN;
  className?: string;
}) {
  return (
    <div className={cx(BREITEN[breite], className)}>
      {/*
        `min-h-[18px]` ist die Höhe einer Mono-Zeile in Grad 12 bei Zeilenhöhe
        1.5 — dieselbe Höhe mit und ohne Inhalt.
      */}
      <div className="min-h-[18px]">
        {kontext ? <Mikrolabel grad={12}>{kontext}</Mikrolabel> : null}
      </div>
      <div className="mt-3.5 flex flex-wrap items-end justify-between gap-x-8 gap-y-4">
        <Titel grad={grad}>{children}</Titel>
        {aktion ? <div className="pb-1.5">{aktion}</div> : null}
      </div>
    </div>
  );
}
